<template>
  <section class="dash-panel advisor-panel">
    <div class="dash-panel__header">
      <div class="dash-panel__heading">
        <span class="dash-panel__eyebrow">第一层</span>
        <h2>指导老师{{ selectedTeamName ? ' · ' + selectedTeamName : '' }}</h2>
      </div>
      <div class="dash-meta">
        <span>{{ teachers.length }} 名老师</span>
      </div>
    </div>
    <div class="advisor-table">
      <div class="advisor-table__head">
        <span>指导老师</span>
        <span>数据量</span>
        <span>触达率</span>
        <span>通话率</span>
        <span>均时长</span>
        <span>异常</span>
      </div>
      <div class="advisor-table__body">
        <button
          v-for="teacher in teachers"
          :key="teacher.teacherName"
          type="button"
          :class="['advisor-table__row', { 'is-active': teacher.teacherName === activeTeacherName }]"
          @click="onTeacherClick(teacher)"
        >
          <span class="advisor-table__name">{{ teacher.teacherName }}</span>
          <span class="advisor-table__num">{{ teacher.customerCount }}</span>
          <span class="advisor-table__rate">{{ teacher.overallReachRate }}<small>%</small></span>
          <span class="advisor-table__rate">{{ teacher.callReachRate }}<small>%</small></span>
          <span class="advisor-table__dur">{{ teacher.avgCallDuration }}<small>分</small></span>
          <span class="advisor-table__warn">{{ teacher.violation }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue'
import { getTeachers } from '@/api'

const props = defineProps({
  campPeriodName: { type: String, default: '' },
  orgUnitId: { type: String, default: '' },
  selectedTeamName: { type: String, default: '' },
  autoSelectFirst: { type: Boolean, default: false },
})

const teachers = ref([])
const activeTeacherName = ref('')
let teacherRequestSeq = 0

const emit = defineEmits(['selectTeacher'])

function onTeacherClick(teacher) {
  activeTeacherName.value = teacher.teacherName
  emit('selectTeacher', teacher.teacherName)
}

function teacherIssueScore(teacher) {
  const violation = Number(teacher.violationCount ?? teacher.suggestedViolationCount ?? teacher.violation ?? 0)
  const review = Number(teacher.reviewCount ?? teacher.needsReviewCount ?? 0)
  return (Number.isFinite(violation) ? violation : 0) * 2 + (Number.isFinite(review) ? review : 0)
}

function pickDefaultTeacher(list) {
  if (!list.length) return null
  return list.reduce((best, teacher) => {
    if (!best) return teacher
    return teacherIssueScore(teacher) > teacherIssueScore(best) ? teacher : best
  }, null)
}

async function fetchTeachers() {
  const requestSeq = ++teacherRequestSeq
  teachers.value = []
  activeTeacherName.value = ''
  if (!props.campPeriodName || !props.orgUnitId) return
  try {
    const params = { campPeriodName: props.campPeriodName }
    if (props.orgUnitId) params.orgUnitId = props.orgUnitId
    const res = await getTeachers(params)
    if (requestSeq !== teacherRequestSeq) return
    if (Array.isArray(res)) {
      teachers.value = sortTeachers(res)
    } else if (res.code === 200 && res.data) {
      teachers.value = sortTeachers(res.data)
    }
    if (props.autoSelectFirst) {
      const selectedStillExists = teachers.value.some((teacher) => teacher.teacherName === activeTeacherName.value)
      if (!selectedStillExists) {
        activeTeacherName.value = ''
      }
      if (teachers.value.length > 0 && !activeTeacherName.value) {
        onTeacherClick(pickDefaultTeacher(teachers.value))
      }
    }
  } catch (err) {
    if (requestSeq !== teacherRequestSeq) return
    console.error('获取指导师列表失败', err)
  }
}

watch(() => [props.campPeriodName, props.orgUnitId, props.selectedTeamName], fetchTeachers, { immediate: true })

function sortTeachers(list) {
  return [...list].sort((left, right) => {
    const countDiff = Number(right.customerCount || 0) - Number(left.customerCount || 0)
    if (countDiff !== 0) return countDiff
    const reachDiff = Number(right.overallReachRate || 0) - Number(left.overallReachRate || 0)
    if (reachDiff !== 0) return reachDiff
    const callDiff = Number(right.callReachRate || 0) - Number(left.callReachRate || 0)
    if (callDiff !== 0) return callDiff
    return String(left.teacherName || '').localeCompare(String(right.teacherName || ''), 'zh-Hans-CN')
  })
}
</script>

<style scoped>
.advisor-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.advisor-table {
  margin-top: 10px;
  border: 1px solid rgba(29, 47, 44, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: rgba(255, 250, 244, 0.4);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.advisor-table__head,
.advisor-table__row {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.56fr) minmax(0, 0.74fr) minmax(0, 0.74fr) minmax(0, 0.84fr) minmax(0, 0.5fr);
  column-gap: 8px;
  align-items: center;
  padding: 0 14px;
}

.advisor-table__head {
  padding-top: 10px;
  padding-bottom: 10px;
  background: rgba(41, 107, 115, 0.06);
  font-size: 0.64rem;
  font-weight: 700;
  color: var(--ink-soft);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.advisor-table__head > span:not(:first-child) {
  text-align: center;
}

.advisor-table__body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.advisor-table__body::-webkit-scrollbar {
  display: none;
}

.advisor-table__row {
  width: 100%;
  appearance: none;
  border: 0;
  border-top: 1px solid rgba(29, 47, 44, 0.05);
  background: transparent;
  padding-top: 11px;
  padding-bottom: 11px;
  text-align: left;
  cursor: pointer;
  transition: background 140ms ease;
  font-family: inherit;
  color: var(--ink);
}

.advisor-table__row:hover {
  background: rgba(41, 107, 115, 0.05);
}

.advisor-table__row.is-active {
  background: rgba(41, 107, 115, 0.1);
}

.advisor-table__name {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.advisor-table__num {
  font-size: 0.78rem;
  font-weight: 600;
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
  text-align: right;
  justify-self: center;
  min-width: 0;
}

.advisor-table__rate {
  font-size: 0.76rem;
  font-weight: 700;
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
  text-align: center;
  color: var(--brand);
  justify-self: center;
  min-width: 0;
}

.advisor-table__rate small {
  font-size: 0.58rem;
  font-weight: 400;
  margin-left: 1px;
  color: var(--ink-soft);
}

.advisor-table__dur {
  font-size: 0.74rem;
  font-weight: 600;
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
  text-align: center;
  justify-self: center;
  min-width: 0;
}

.advisor-table__dur small {
  font-size: 0.58rem;
  font-weight: 400;
  margin-left: 2px;
  color: var(--ink-soft);
}

.advisor-table__warn {
  font-size: 0.76rem;
  font-weight: 600;
  text-align: center;
  justify-self: center;
  min-width: 0;
}

.dash-panel__heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dash-panel__header {
  align-items: flex-start;
}

@media (max-width: 860px) {
  .advisor-table__head {
    display: none;
  }

  .advisor-table__body {
    max-height: none;
  }

  .advisor-table__row {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    padding: 10px 14px;
  }

  .advisor-table__name {
    width: 100%;
    font-size: 0.92rem;
    margin-bottom: 2px;
  }

  .advisor-table__row > .advisor-table__num,
  .advisor-table__row > .advisor-table__rate,
  .advisor-table__row > .advisor-table__dur,
  .advisor-table__row > .advisor-table__warn {
    font-size: 0.72rem;
    color: var(--ink-soft);
  }
}
</style>
